import * as Api from '@ViewModels';
import { FavoriteFor, TagViewModel } from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { IModalContext, ModalChildComponentContextKey } from '../../../../../models';
import { useEventLogging } from '../../../../../models/Logging';
import { useCreateTag } from '../../../../../queries';
import { useTagLockedMutation } from '../../../../../queries/Tag/useTagLockedMutation';
import { Checkbox } from '../../../../components-v2/Checkbox';
import { Column, Row } from '../../../../components-v2/PageLayout';
import { ThemeColors } from '../../../../components-v2/Theme/theme';
import { ColorPicker } from '../../../../containers/PeopleV2/TagsContainer/ColorPicker/ColorPicker';
import { baseStyleSheet } from '../../../../styles/styles';
import { toSentenceCase } from '../../../../utils/toSentenceCase';
import { DeprecatedCloseButton } from '../../../DeprecatedCloseButton';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { IModalProps, Modal } from '../../../Modal';
import { TextInput } from '../../../TextInput';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../Tooltip';
import { InfoIcon } from '../../../svgs/icons/InfoIcon';
import { styleSheet } from './styles';

const LOCK_EXPLANATION =
	"When locked, only admins can add or remove this tag from contacts. Locking doesn't affect tags applied through integrations, automations, or surveys.";

interface IProps extends IModalContext<TagViewModel> {
	impersonationContext?: Api.IImpersonationContext;
}

function _AddTag({ parentModal, impersonationContext }: IProps) {
	const { logInput, logApiError } = useEventLogging('AddTag');
	const [errorMessage, setErrorMessage] = React.useState<string>();
	const [tagValue, setTagValue] = React.useState<string>('');
	const [tagColor, setTagColor] = React.useState<keyof ThemeColors['pill']>('gray');
	const [isLocked, setIsLocked] = React.useState<boolean>(false);
	const addTagMutation = useCreateTag({
		impersonationContext,
		onError: error => {
			logApiError('Add-Error', error);
			setErrorMessage(error.systemMessage);
		},
	});
	// Per the API contract, isLocked is settable only via PUT /tag/{id}/isLocked, so the
	// "lock at creation" UX is a two-step: POST /tag, then PUT lock if checked.
	const tagLockedMutation = useTagLockedMutation({});
	const isPending = addTagMutation.isPending || tagLockedMutation.isPending;
	const onRequestClose = (result?: TagViewModel, cancel = false) => {
		parentModal?.onRequestClose(result, cancel);
	};
	const onCloseClicked = () => {
		onRequestClose(null, true);
	};
	const onTagValueChanged = (ev: React.ChangeEvent<HTMLInputElement>) => {
		setErrorMessage(null);
		setTagValue(ev.target.value);
	};
	const onTagValueTextInputFocus = () => {
		setErrorMessage(null);
	};
	const onAddButtonClicked = async () => {
		const value = tagValue.trim();
		if (!value) {
			setErrorMessage('Please enter a value for this tag');
			return;
		}
		logInput('Add', 'Click');
		let created: TagViewModel;
		try {
			created = await addTagMutation.mutateAsync({
				accountTag: {
					favoriteFor: FavoriteFor.None,
					name: value,
					color: toSentenceCase(tagColor) as keyof ThemeColors['pill'],
				},
			});
		} catch {
			// onError handler already set errorMessage; leave the modal open.
			return;
		}
		if (isLocked && created?.id) {
			try {
				await tagLockedMutation.mutateAsync({
					tagId: created.id,
					isLocked: true,
					impersonationContext,
				});
			} catch (lockError) {
				// The tag was created successfully but the lock step failed. Surface the
				// failure non-blockingly and still close — the new tag exists in the table
				// and the admin can lock it from the row.
				logApiError('AddTag-LockSet', lockError);
				toast.warning(`${value} was created but could not be locked. Try locking it from the tag list.`);
			}
		}
		onRequestClose(created, false);
	};
	const onTagValueTextInputKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
		if (ev.key === 'Enter') {
			onAddButtonClicked();
		}
	};
	return (
		<Column styles={[styleSheet.addTag]}>
			<Row styles={[styleSheet.addTagHeader]}>
				<DeprecatedCloseButton disabled={isPending} onClick={onCloseClicked} />
			</Row>
			<Row>
				<span className={css(styleSheet.addTagTitle)}>Add Tag</span>
			</Row>
			<Row styles={[styleSheet.inputRow]} fullWidth>
				<TextInput
					autoFocus={true}
					className={css(styleSheet.addTagTextInput)}
					disabled={isPending}
					inputId='add-tag-input'
					onChange={onTagValueChanged}
					onFocus={onTagValueTextInputFocus}
					onKeyDown={onTagValueTextInputKeyDown}
					placeholder='Enter the new tag here'
					type='text'
					value={tagValue}
				/>
				<ColorPicker onColorChange={setTagColor} color={tagColor} />
			</Row>
			<Row styles={[styleSheet.lockRow]}>
				<label className={css(styleSheet.lockLabel)}>
					<Checkbox
						checked={isLocked}
						onCheckedChange={checked => setIsLocked(checked === true)}
						disabled={isPending}
					/>
					<span>Lock this tag</span>
				</label>
				<Tooltip>
					<TooltipTrigger asChild>
						<span className={css(styleSheet.lockInfoIcon)} aria-label='What does locking do?'>
							<InfoIcon />
						</span>
					</TooltipTrigger>
					<TooltipContent styles={[styleSheet.lockTooltipContent]}>{LOCK_EXPLANATION}</TooltipContent>
				</Tooltip>
			</Row>
			<div className={css(styleSheet.addTagFooter)}>
				<button
					className={css(styleSheet.addTagFooterAddButton, baseStyleSheet.ctaButton)}
					disabled={isPending}
					onClick={onAddButtonClicked}
				>
					<span>Add</span>
				</button>
				{errorMessage ? (
					<span className={css(styleSheet.addTagFooterErrorMessage, baseStyleSheet.truncateText)} title={errorMessage}>
						{errorMessage}
					</span>
				) : null}
			</div>
			{isPending ? <LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='large' /> : null}
		</Column>
	);
}

const AddTagAsObserver = observer(_AddTag);
export const AddTag = inject(ModalChildComponentContextKey)(AddTagAsObserver);

export const AddTagModal = (props: IProps & { modalProps?: IModalProps }) => {
	const { modalProps = {}, ...restProps } = props;
	return (
		<Modal {...modalProps}>
			<AddTag {...restProps} />
		</Modal>
	);
};
